import { zodResolver } from '@hookform/resolvers/zod';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AUDIO_AUTH_METHODS,
  ELIGIBILITY_PROGRAMS,
  FPHA_PROGRAM,
  PUBLIC_HOUSING_TYPES,
  SCHOOL_PROGRAM,
  LIFELINE_BENEFICIARIES,
  LIFELINE_CONSENTS,
  LIFELINE_DOCUMENT_KINDS,
  US_STATES,
  lifelinePersonalSchema,
} from '@app/shared';
import type { LifelineApplicationDto, LifelinePersonalInput, LifelineStage } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { ApiError, api } from '../../lib/api';
import { formatCents } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { customerPath } from '../customers/parts';
import { BASE, useLifelineApplication, useLifelinePlans, useLifelineStep } from './api';

/** The steps along the top, as the owner's layout: 1 Personal Info ... 4 Enroll. */
const STEPS: Array<[label: string, stage: LifelineStage | 'personal']> = [
  ['1 Personal Info', 'personal'],
  ['2 Consent', 'consent'],
  ['3 Upload docs', 'documents'],
  ['Audio Auth', 'audio'],
  ['3 National Verifier', 'verifier'],
  ['4 Enroll', 'enroll'],
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
].map((m, i) => ({ value: String(i + 1), label: m }));
const DAYS = Array.from({ length: 31 }, (_v, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));
const thisYear = new Date().getFullYear();
const YEARS = Array.from({ length: 110 }, (_v, i) => String(thisYear - i)).map((y) => ({
  value: y,
  label: y,
}));
const STATE_OPTIONS = US_STATES.map(([code, name]) => ({
  value: code,
  label: `${code} - ${name}`,
}));

const fieldError = (err: unknown, path: string) =>
  err instanceof ApiError ? err.details.find((d) => d.path === path)?.message : undefined;

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box component="section" aria-label={title} sx={{ mb: 2 }}>
      <Typography variant="h3" color="primary" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

const grid = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
  mb: 2,
};

/* ------------------------------ 1 Personal Info ---------------------------- */

const EMPTY: LifelinePersonalInput = {
  firstName: '',
  lastName: '',
  email: '',
  primaryPhone: '',
  dobMonth: '' as never,
  dobDay: '' as never,
  dobYear: '' as never,
  ssnLast4: '',
  tribalId: '',
  eligibilityProgram: '',
  publicHousingType: '',
  schoolName: '',
  beneficiary: 'Myself',
  streetName: '',
  apartment: '',
  zip: '',
  city: '',
  mailingDifferent: false,
};

function fromApplication(a: LifelineApplicationDto): LifelinePersonalInput {
  const [y, m, d] = a.dob.split('-').map(Number);
  const dep = a.dependent?.dob.split('-').map(Number);
  return {
    ...EMPTY,
    firstName: a.firstName,
    lastName: a.lastName,
    email: a.email,
    primaryPhone: a.primaryPhone,
    dobMonth: String(m) as never,
    dobDay: String(d) as never,
    dobYear: String(y) as never,
    // The SSN is never shown back: left empty, the stored one is kept.
    tribalId: a.tribalId,
    eligibilityProgram: a.eligibilityProgram,
    publicHousingType: a.publicHousingType,
    schoolName: a.schoolName,
    beneficiary: a.beneficiary as LifelinePersonalInput['beneficiary'],
    dependentFirstName: a.dependent?.firstName ?? '',
    dependentLastName: a.dependent?.lastName ?? '',
    dependentDobMonth: (dep ? String(dep[1]) : '') as never,
    dependentDobDay: (dep ? String(dep[2]) : '') as never,
    dependentDobYear: (dep ? String(dep[0]) : '') as never,
    streetName: a.address.street,
    apartment: a.address.apartment,
    zip: a.address.zip,
    city: a.address.city,
    mailingDifferent: Boolean(a.mailing),
    mailingStreet: a.mailing?.street ?? '',
    mailingApartment: a.mailing?.apartment ?? '',
    mailingCity: a.mailing?.city ?? '',
    mailingState: a.mailing?.state ?? '',
    mailingZip: a.mailing?.zip ?? '',
  };
}

function PersonalInfo({ application }: { application?: LifelineApplicationDto }) {
  const notify = useNotify();
  const navigate = useNavigate();
  // An edit may leave the SSN empty (the stored one is kept); a new application needs it or a Tribal ID.
  const editing = Boolean(application?.ssnMasked);
  const { control, handleSubmit, setError, setValue, watch } = useForm<LifelinePersonalInput>({
    defaultValues: application ? fromApplication(application) : EMPTY,
    resolver: editing
      ? undefined
      : (zodResolver(lifelinePersonalSchema) as Resolver<LifelinePersonalInput>),
  });
  const save = useLifelineStep((values: LifelinePersonalInput) =>
    application
      ? api.put<LifelineApplicationDto>(`${BASE}/${application.id}/personal`, values)
      : api.post<LifelineApplicationDto>(BASE, values),
  );
  const submit = handleSubmit(async (values) => {
    try {
      const saved = await save.mutateAsync(values);
      notify.success(application ? 'Personal information saved.' : 'Lifeline application started.');
      if (!application) navigate(`/lifeline/${saved.id}`, { replace: true });
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });
  const dependent = watch('beneficiary') === 'Child or Dependent in household';
  const featureOn = useFeatureReleased();
  const details = featureOn('lifeline-program-details');
  const program = watch('eligibilityProgram');
  const programs = details
    ? ELIGIBILITY_PROGRAMS
    : ELIGIBILITY_PROGRAMS.filter((p) => p !== SCHOOL_PROGRAM);
  // School Lunch/Breakfast is a child's benefit: choosing it picks Child or Dependent, once; the
  // user may change it back.
  const lastProgram = useRef(program);
  useEffect(() => {
    if (program === SCHOOL_PROGRAM && lastProgram.current !== SCHOOL_PROGRAM)
      setValue('beneficiary', 'Child or Dependent in household');
    lastProgram.current = program;
  }, [program, setValue]);
  const mailing = watch('mailingDifferent');
  const dob = (prefix: '' | 'dependent') => {
    const key = (part: 'Month' | 'Day' | 'Year') =>
      (prefix ? `dependentDob${part}` : `dob${part}`) as keyof LifelinePersonalInput;
    return (
      <Box
        sx={{
          gridColumn: { md: 'span 2' },
          display: 'grid',
          gap: 1,
          gridTemplateColumns: 'repeat(3, 1fr)',
        }}
      >
        <SelectInput
          control={control}
          name={key('Month')}
          label={prefix ? 'Dependent Month' : 'Month'}
          emptyLabel="--Select Month--"
          options={MONTHS}
        />
        <SelectInput
          control={control}
          name={key('Day')}
          label={prefix ? 'Dependent Day' : 'Day'}
          emptyLabel="--Select Day--"
          options={DAYS}
        />
        <SelectInput
          control={control}
          name={key('Year')}
          label={prefix ? 'Dependent Year' : 'Year'}
          emptyLabel="--Select Year--"
          options={YEARS}
        />
      </Box>
    );
  };
  return (
    <Box component="form" noValidate onSubmit={(e) => void submit(e)}>
      <Section title="Personal Information">
        <Box sx={grid}>
          <TextInput control={control} name="firstName" label="First Name" required />
          <TextInput control={control} name="lastName" label="Last Name" required />
          <TextInput control={control} name="email" label="Email" type="email" />
          <TextInput
            control={control}
            name="primaryPhone"
            label="Primary Phone"
            required
            slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
          />
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Date Of Birth
        </Typography>
        <Box sx={grid}>
          {dob('')}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, gridColumn: { md: 'span 2' } }}>
            <TextInput
              control={control}
              name="ssnLast4"
              label="SSN (Last 4 Digits)"
              placeholder={editing ? `Stored ${application!.ssnMasked}` : 'Last 4 of SSN'}
              slotProps={{ htmlInput: { maxLength: 4, inputMode: 'numeric', autoComplete: 'off' } }}
              sx={{ flex: 1 }}
            />
            <Typography sx={{ fontWeight: 600 }}>OR</Typography>
            <TextInput control={control} name="tribalId" label="Tribal ID" sx={{ flex: 1 }} />
          </Box>
          <Box sx={{ gridColumn: { md: 'span 2' } }}>
            <SelectInput
              control={control}
              name="eligibilityProgram"
              label="Eligibility Program"
              emptyLabel="--select--"
              options={toOptions(programs)}
              required
            />
          </Box>
          {details && program === FPHA_PROGRAM ? (
            <Box sx={{ gridColumn: { md: 'span 2' } }}>
              <SelectInput
                control={control}
                name="publicHousingType"
                label="Public Housing Type"
                emptyLabel="Choose"
                options={toOptions(PUBLIC_HOUSING_TYPES)}
                required
              />
            </Box>
          ) : null}
          {details && program === SCHOOL_PROGRAM ? (
            <Box sx={{ gridColumn: { md: 'span 2' } }}>
              <TextInput control={control} name="schoolName" label="School" required />
            </Box>
          ) : null}
          <Controller
            control={control}
            name="beneficiary"
            render={({ field }) => (
              <Box sx={{ gridColumn: { md: 'span 2' } }}>
                <Typography variant="body2" id="beneficiary-label" sx={{ fontWeight: 600 }}>
                  Who receives the benefit?
                </Typography>
                <RadioGroup
                  row
                  aria-labelledby="beneficiary-label"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {LIFELINE_BENEFICIARIES.map((b) => (
                    <FormControlLabel
                      key={b}
                      value={b}
                      control={<Radio size="small" />}
                      label={b}
                    />
                  ))}
                </RadioGroup>
              </Box>
            )}
          />
        </Box>
        {dependent ? (
          <>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Child or Dependent
            </Typography>
            <Box sx={grid}>
              <TextInput
                control={control}
                name="dependentFirstName"
                label="Dependent First Name"
                required
              />
              <TextInput
                control={control}
                name="dependentLastName"
                label="Dependent Last Name"
                required
              />
              {dob('dependent')}
            </Box>
          </>
        ) : null}
      </Section>
      <Section title="Address">
        <Box sx={grid}>
          <TextInput control={control} name="streetName" label="Street Name" required />
          <TextInput control={control} name="apartment" label="Apartment or Suite" />
          <TextInput
            control={control}
            name="zip"
            label="ZIP Code"
            required
            slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
          />
          <TextInput control={control} name="city" label="City" required />
          <TextField
            label="State"
            value={application?.address.state ?? ''}
            placeholder="From the ZIP Code"
            disabled
            helperText={application ? undefined : 'Set from the ZIP Code'}
          />
          <Controller
            control={control}
            name="mailingDifferent"
            render={({ field }) => (
              <FormControlLabel
                sx={{ gridColumn: { md: 'span 2' } }}
                control={
                  <Checkbox
                    checked={Boolean(field.value)}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Check here if mailing address is different"
              />
            )}
          />
        </Box>
        {mailing ? (
          <Box sx={grid}>
            <TextInput
              control={control}
              name="mailingStreet"
              label="Mailing Street Name"
              required
            />
            <TextInput
              control={control}
              name="mailingApartment"
              label="Mailing Apartment or Suite"
            />
            <TextInput control={control} name="mailingCity" label="Mailing City" required />
            <SelectInput
              control={control}
              name="mailingState"
              label="Mailing State"
              emptyLabel="Select"
              options={STATE_OPTIONS}
              required
            />
            <TextInput
              control={control}
              name="mailingZip"
              label="Mailing ZIP Code"
              required
              slotProps={{ htmlInput: { maxLength: 5 } }}
            />
          </Box>
        ) : null}
      </Section>
      <Box sx={{ textAlign: 'right' }}>
        <Button type="submit" variant="contained" disabled={save.isPending}>
          {application ? 'Save' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
}

/* --------------------------------- 2 Consent ------------------------------- */

function Consent({ application }: { application: LifelineApplicationDto }) {
  const notify = useNotify();
  const [accepted, setAccepted] = useState(
    LIFELINE_CONSENTS.map(() => Boolean(application.consent)),
  );
  const [signature, setSignature] = useState(application.consent?.signature ?? '');
  const save = useLifelineStep(() =>
    api.put<LifelineApplicationDto>(`${BASE}/${application.id}/consent`, { accepted, signature }),
  );
  return (
    <Section title="Consent">
      <Typography sx={{ mb: 1 }}>
        Read each statement to the applicant and tick it when they agree.
      </Typography>
      {LIFELINE_CONSENTS.map((text, i) => (
        <FormControlLabel
          key={text}
          sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}
          control={
            <Checkbox
              checked={accepted[i]}
              onChange={(e) =>
                setAccepted(accepted.map((v, j) => (j === i ? e.target.checked : v)))
              }
            />
          }
          label={text}
        />
      ))}
      {fieldError(save.error, 'accepted') ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {fieldError(save.error, 'accepted')}
        </Alert>
      ) : null}
      <TextField
        label="Signature (applicant's full name)"
        value={signature}
        onChange={(e) => setSignature(e.target.value)}
        error={Boolean(fieldError(save.error, 'signature'))}
        helperText={fieldError(save.error, 'signature')}
        sx={{ minWidth: 320 }}
        required
      />
      <Box sx={{ textAlign: 'right', mt: 2 }}>
        <Button
          variant="contained"
          disabled={save.isPending}
          onClick={() =>
            void save
              .mutateAsync(undefined)
              .then(() => notify.success('Consent saved.'))
              .catch((err) =>
                fieldError(err, 'accepted') || fieldError(err, 'signature')
                  ? undefined
                  : notify.error(err),
              )
          }
        >
          Next
        </Button>
      </Box>
    </Section>
  );
}

/* ------------------------------- 3 Upload docs ----------------------------- */

function Documents({ application }: { application: LifelineApplicationDto }) {
  const notify = useNotify();
  const [kind, setKind] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const upload = useLifelineStep(() => {
    const form = new FormData();
    form.append('kind', kind);
    if (file) form.append('file', file);
    return api.upload<LifelineApplicationDto>(`${BASE}/${application.id}/documents`, form);
  });
  const remove = useLifelineStep((docId: string) =>
    api.delete<LifelineApplicationDto>(`${BASE}/${application.id}/documents/${docId}`),
  );
  const done = useLifelineStep(() =>
    api.post<LifelineApplicationDto>(`${BASE}/${application.id}/documents/done`),
  );
  return (
    <Section title="Upload docs">
      <Typography sx={{ mb: 1.5 }}>
        Upload the proof of eligibility (benefit letter or card) and, when asked, proof of identity
        or address. PDF, JPG or PNG, up to 5 MB each. Documents are optional here; the National
        Verifier may ask for them.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
        <TextField
          select
          label="Document Type"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          sx={{ minWidth: 220 }}
          error={Boolean(fieldError(upload.error, 'kind'))}
          helperText={fieldError(upload.error, 'kind')}
        >
          {LIFELINE_DOCUMENT_KINDS.map((k) => (
            <MenuItem key={k} value={k}>
              {k}
            </MenuItem>
          ))}
        </TextField>
        <Box>
          <Button variant="outlined" component="label">
            {file ? file.name : 'Choose file'}
            <input
              hidden
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              aria-label="Document file"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          {fieldError(upload.error, 'file') ? (
            <Typography color="error" variant="caption" display="block">
              {fieldError(upload.error, 'file')}
            </Typography>
          ) : null}
        </Box>
        <Button
          variant="contained"
          disabled={upload.isPending}
          onClick={() =>
            void upload
              .mutateAsync(undefined)
              .then(() => {
                setFile(null);
                notify.success('Document uploaded.');
              })
              .catch((err) =>
                fieldError(err, 'kind') || fieldError(err, 'file') ? undefined : notify.error(err),
              )
          }
        >
          Upload
        </Button>
      </Box>
      {application.documents.length ? (
        <Box component="ul" aria-label="Uploaded documents" sx={{ pl: 2 }}>
          {application.documents.map((d) => (
            <li key={d.id}>
              <MuiLink
                href={api.url(`${BASE}/${application.id}/documents/${d.id}`)}
                target="_blank"
                rel="noreferrer"
              >
                {d.kind}: {d.fileName}
              </MuiLink>
              <IconButton
                size="small"
                aria-label={`Remove ${d.fileName}`}
                onClick={() => void remove.mutateAsync(d.id).catch((err) => notify.error(err))}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </li>
          ))}
        </Box>
      ) : (
        <Typography color="text.secondary">No document uploaded yet.</Typography>
      )}
      <Box sx={{ textAlign: 'right', mt: 2 }}>
        <Button
          variant="contained"
          disabled={done.isPending}
          onClick={() => void done.mutateAsync(undefined).catch((err) => notify.error(err))}
        >
          Next
        </Button>
      </Box>
    </Section>
  );
}

/* -------------------------------- Audio Auth ------------------------------- */

function AudioAuth({ application }: { application: LifelineApplicationDto }) {
  const notify = useNotify();
  const [method, setMethod] = useState(application.audio?.method ?? '');
  const [reference, setReference] = useState(application.audio?.reference ?? '');
  const save = useLifelineStep(() =>
    api.put<LifelineApplicationDto>(`${BASE}/${application.id}/audio`, { method, reference }),
  );
  return (
    <Section title="Audio Auth">
      <Typography sx={{ mb: 1.5 }}>
        For an application taken by phone, the applicant agrees on a recorded line: give the
        recording reference. Not needed for an application signed in person.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Authorization"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          sx={{ minWidth: 220 }}
          error={Boolean(fieldError(save.error, 'method'))}
          helperText={fieldError(save.error, 'method')}
        >
          {AUDIO_AUTH_METHODS.map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </TextField>
        {method === 'Recorded call' ? (
          <TextField
            label="Call recording reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            error={Boolean(fieldError(save.error, 'reference'))}
            helperText={fieldError(save.error, 'reference')}
          />
        ) : null}
      </Box>
      <Box sx={{ textAlign: 'right', mt: 2 }}>
        <Button
          variant="contained"
          disabled={save.isPending}
          onClick={() =>
            void save
              .mutateAsync(undefined)
              .catch((err) =>
                fieldError(err, 'method') || fieldError(err, 'reference')
                  ? undefined
                  : notify.error(err),
              )
          }
        >
          Next
        </Button>
      </Box>
    </Section>
  );
}

/* ----------------------------- National Verifier --------------------------- */

const VERIFIER_COLOR = { Approved: 'success', Pending: 'warning', Rejected: 'error' } as const;

function Verifier({ application }: { application: LifelineApplicationDto }) {
  const notify = useNotify();
  const featureOn = useFeatureReleased();
  const v = application.verifier;
  const [applicationId, setApplicationId] = useState(v && !v.simulated ? v.applicationId : '');
  const check = useLifelineStep(() =>
    api.post<LifelineApplicationDto>(`${BASE}/${application.id}/verify`, { applicationId }),
  );
  return (
    <Section title="National Verifier">
      {featureOn('lifeline-connection') ? (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            The applicant applies with the National Verifier at lifelinesupport.org and gets an
            Application ID; enter it here. On a company that has not connected Lifeline (NLAD) yet
            the answer is simulated: it follows 212Ez's test rules and its application ID starts
            with SIM-NV-.
          </Alert>
          <TextField
            label="National Verifier Application ID"
            value={applicationId}
            onChange={(e) => setApplicationId(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
        </>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Simulated: the National Verifier is not connected yet. The answer follows 212Ez's test
          rules and its application ID starts with SIM-NV-. No data leaves 212Ez.
        </Alert>
      )}
      {v ? (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }} aria-label="Verifier result">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip color={VERIFIER_COLOR[v.status]} label={v.status} />
            <Typography>Application ID: {v.applicationId || '-'}</Typography>
          </Box>
          {v.reason ? <Typography>{v.reason}</Typography> : null}
          <Typography variant="caption" color="text.secondary">
            Checked {new Date(v.checkedAt).toLocaleString()}
          </Typography>
        </Paper>
      ) : null}
      {v?.status === 'Rejected' ? (
        <Typography sx={{ mb: 2 }}>
          The application is rejected. Correct the personal information if it was wrong, then check
          again.
        </Typography>
      ) : null}
      <Box sx={{ textAlign: 'right' }}>
        <Button
          variant="contained"
          disabled={check.isPending}
          onClick={() => void check.mutateAsync(undefined).catch((err) => notify.error(err))}
        >
          {v ? 'Check again' : 'Check eligibility'}
        </Button>
      </Box>
    </Section>
  );
}

/* --------------------------------- 4 Enroll -------------------------------- */

function Enroll({ application }: { application: LifelineApplicationDto }) {
  const notify = useNotify();
  const navigate = useNavigate();
  const plans = useLifelinePlans(application.id, true);
  const [planId, setPlanId] = useState('');
  const enroll = useLifelineStep(() =>
    api.put<LifelineApplicationDto>(`${BASE}/${application.id}/enroll`, { planId }),
  );
  return (
    <Section title="Enroll">
      <Typography sx={{ mb: 1.5 }}>
        Approved. Choose the Lifeline plan; the order then goes on to the SIM handover and the
        payment, as for prepaid.
      </Typography>
      <TextField
        select
        label="Lifeline Plan"
        value={planId}
        onChange={(e) => setPlanId(e.target.value)}
        sx={{ minWidth: 320 }}
        error={Boolean(fieldError(enroll.error, 'planId'))}
        helperText={
          fieldError(enroll.error, 'planId') ??
          (plans.data?.length === 0 ? 'No Lifeline plan is LIVE in this state.' : undefined)
        }
      >
        {(plans.data ?? []).map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.name} ({formatCents(p.priceCents)})
          </MenuItem>
        ))}
      </TextField>
      <Box sx={{ textAlign: 'right', mt: 2 }}>
        <Button
          variant="contained"
          disabled={enroll.isPending || !planId}
          onClick={() =>
            void enroll
              .mutateAsync(undefined)
              .then(() => navigate(`/enrollments/${application.id}/step/3`))
              .catch((err) => (fieldError(err, 'planId') ? undefined : notify.error(err)))
          }
        >
          Enroll
        </Button>
      </Box>
    </Section>
  );
}

/* ---------------------------------- Page ----------------------------------- */

function Steps({ active }: { active: number }) {
  return (
    <Stepper activeStep={active} alternativeLabel sx={{ mb: 3 }} aria-label="Lifeline steps">
      {STEPS.map(([label]) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}

/** "New application" (PC1164): Personal Info; the application then opens on its own page. */
export function LifelineNewPage() {
  return (
    <Box>
      <PageHeader title="Lifeline Enrollment" code="PC1164" />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Steps active={0} />
        <PersonalInfo />
      </Paper>
    </Box>
  );
}

/** One application: the step it is at, and Personal Info to correct it while the steps are open. */
export function LifelineApplicationPage() {
  const { id } = useParams();
  const app = useLifelineApplication(id);
  const [editing, setEditing] = useState(false);
  if (app.isPending) return <LoadingState label="Loading the application…" />;
  if (app.isError) return <ErrorState error={app.error} onRetry={() => void app.refetch()} />;
  const a = app.data;
  const stageIndex = a.stage ? STEPS.findIndex(([, s]) => s === a.stage) : STEPS.length;
  const panel: Record<LifelineStage, ReactNode> = {
    consent: <Consent application={a} />,
    documents: <Documents application={a} />,
    audio: <AudioAuth application={a} />,
    verifier: <Verifier application={a} />,
    enroll: <Enroll application={a} />,
  };
  return (
    <Box>
      <PageHeader
        title={`Lifeline Enrollment ${a.enrollmentId}`}
        code="PC1164"
        actions={
          a.stage ? (
            <Button onClick={() => setEditing((v) => !v)}>
              {editing ? 'Back to the step' : '1 Personal Info'}
            </Button>
          ) : null
        }
      />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Steps active={editing ? 0 : stageIndex} />
        <Typography sx={{ mb: 2 }}>
          {a.firstName} {a.lastName}, {a.address.city}, {a.address.state} {a.address.zip}
          {a.ssnMasked ? ` · SSN ${a.ssnMasked}` : ''}
          {a.tribalId ? ` · Tribal ID ${a.tribalId}` : ''} · {a.eligibilityProgram}
        </Typography>
        {editing ? (
          <PersonalInfo application={a} />
        ) : a.stage ? (
          panel[a.stage]
        ) : a.status === 'Complete' ? (
          <Alert severity="success">
            Enrolled on {a.plan?.name}.{' '}
            {a.customerRef ? (
              <MuiLink component={Link} to={customerPath(a.customerRef)}>
                Open customer {a.customerId}
              </MuiLink>
            ) : null}
          </Alert>
        ) : (
          <Alert
            severity="info"
            action={
              <Button component={Link} to={`/enrollments/${a.id}/step/${a.step ?? 3}`}>
                Continue
              </Button>
            }
          >
            Plan {a.plan?.name} chosen. The order continues with step {a.step ?? 3} (
            {a.step === 4 ? 'payment' : 'handover'}).
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
